"use client";

import Image from "next/image";
import { MARKETING_PARTNER_BRANDS } from "@/constants/marketingPartnerBrands";
import clsx from "clsx";

export type MarketingPartnerTickerProps = {
  ariaLabel: string;
  label: string;
  className?: string;
  /** Tighter vertical spacing for landing pages directly below the hero. */
  compact?: boolean;
  /** Top and bottom border dividers separating this block from adjacent sections. */
  showDivider?: boolean;
};

const TICKER_LOGOS = [...MARKETING_PARTNER_BRANDS, ...MARKETING_PARTNER_BRANDS];

export function MarketingPartnerTicker({
  ariaLabel,
  label,
  className,
  compact = false,
  showDivider = false,
}: Readonly<MarketingPartnerTickerProps>) {
  return (
    <section
      aria-label={ariaLabel}
      className={clsx(
        "w-full px-4",
        compact ? "pt-6 pb-8 md:pt-8 md:pb-10" : "py-12 md:py-16",
        showDivider && "border-y border-black/10",
        className,
      )}
    >
      <p
        className={clsx(
          "mx-auto max-w-6xl text-center font-monument text-sm leading-relaxed text-gail-gray md:text-base",
          compact ? "mb-6 md:mb-6" : "mb-10 md:mb-8",
        )}
      >
        {label}
      </p>
      <div className="marketing-partner-ticker relative mx-auto w-full max-w-6xl overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-linear-to-r from-marketing-surface to-transparent sm:w-20 md:w-28"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 right-0 z-10 w-12 bg-linear-to-l from-marketing-surface to-transparent sm:w-20 md:w-28"
        />

        <div className="overflow-hidden">
          <div className="marketing-partner-ticker-track flex items-center">
            {TICKER_LOGOS.map((brand, index) => (
              <div
                key={`${brand.src}-${index}`}
                className="flex shrink-0 items-center px-6 md:px-10"
              >
                <div className="flex h-11 w-32 items-center justify-center md:h-12 md:w-36">
                  <Image
                    src={brand.src}
                    alt={brand.alt}
                    width={brand.width}
                    height={brand.height}
                    className="max-h-full max-w-full object-contain brightness-0 opacity-50 transition-opacity duration-200 hover:opacity-100"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
